import { FC, useCallback, useState } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import { IconButton, Radio, Skeleton, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { CardActions } from './CardActions';
import { CardDeleteConfirmActions } from './CardDeleteConfirmActions';

import { PAYMENT_GATEWAY, PAYMENT_STEP, QUERY_KEY } from '~common';
import { CampusCardItem } from '~components/atoms/new-step-three/PaymentMethodItem';
import { LocalizationTypography } from '~components/organisms/localization';
import { useMutationDeletePaymentMethod } from '~hooks';
import {
  isSelectedCampusCard,
  usePaymentStepContext,
  useStationInformationContext,
  useUserPaymentContext,
} from '~providers';

export const CampusCardSection: FC = () => {
  const queryClient = useQueryClient();
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  const {
    handleSetPaymentStep,
    selectedPaymentMethod,
    handleSetSelectedPaymentMethod,
    selectedPaymentMethodToDelete,
    handleSetSelectedPaymentMethodToDelete,
  } = usePaymentStepContext();

  const {
    userCampusCards: cards,
    isFetchingPaymentInfo,
    isFetchedPaymentInfo,
  } = useUserPaymentContext();
  const { campusCard } = useStationInformationContext();
  const isLoading = isFetchingPaymentInfo && !isFetchedPaymentInfo;

  const selectedCampusCardId = isSelectedCampusCard(selectedPaymentMethod)
    ? selectedPaymentMethod.card.id
    : null;

  const {
    mutateAsync: deletePaymentMethod,
    isLoading: isDeletingPaymentMethod,
  } = useMutationDeletePaymentMethod({
    onErrorCallback: () => {
      handleSetSelectedPaymentMethodToDelete(null);
    },
  });

  const handleEnterDeleteMode = useCallback(() => {
    setIsDeleteMode(true);
    handleSetSelectedPaymentMethodToDelete(null);
  }, [handleSetSelectedPaymentMethodToDelete]);

  const handleCancelDeleteMode = useCallback(() => {
    setIsDeleteMode(false);
    handleSetSelectedPaymentMethodToDelete(null);
  }, [handleSetSelectedPaymentMethodToDelete]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPaymentMethodToDelete) {
      return;
    }

    try {
      await deletePaymentMethod({
        id: selectedPaymentMethodToDelete.id,
        gateway: PAYMENT_GATEWAY.CAMPUS_CARD,
      });
    } catch {
      // Already toasted by the mutation; the card is still there.
      return;
    }

    if (selectedCampusCardId === selectedPaymentMethodToDelete.id) {
      handleSetSelectedPaymentMethod('WALLET');
    }

    handleSetSelectedPaymentMethodToDelete(null);
    setIsDeleteMode(false);
    await queryClient.invalidateQueries([QUERY_KEY.GET_USER_PAYMENT_METHODS]);
  }, [
    deletePaymentMethod,
    handleSetSelectedPaymentMethod,
    handleSetSelectedPaymentMethodToDelete,
    queryClient,
    selectedCampusCardId,
    selectedPaymentMethodToDelete,
  ]);

  return (
    <>
      <Stack direction="row">
        <IconButton
          aria-label="back"
          size="small"
          color="inherit"
          sx={{ paddingY: 0 }}
          onClick={() =>
            handleSetPaymentStep(PAYMENT_STEP.SELECT_PAYMENT_METHOD)
          }
        >
          <ArrowBackIos />
        </IconButton>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.campusCard.title"
          defaultText="Campus Card"
          variant="Poppins/Heading-3"
        />
      </Stack>

      {/* CAMPUS CARD LIST */}
      <Stack direction="column">
        {isLoading ? (
          <>
            <Skeleton variant="rectangular" height={56} sx={{ my: 0.5 }} />
            <Skeleton variant="rectangular" height={56} sx={{ my: 0.5 }} />
          </>
        ) : cards.length > 0 ? (
          cards.map((card) => {
            const isDisabled =
              campusCard.enabled &&
              !!campusCard.institutionId &&
              card.institutionUuid !== campusCard.institutionId;
            const isItemDisabled = isDeleteMode ? false : isDisabled;
            return (
              <CampusCardItem
                key={card.id}
                enableDivider={false}
                card={card}
                disabled={isItemDisabled}
                action={
                  <Radio
                    checked={
                      isDeleteMode
                        ? selectedPaymentMethodToDelete?.id === card.id
                        : selectedCampusCardId === card.id
                    }
                    value={card.id}
                    name="radio-buttons"
                    inputProps={{ 'aria-label': PAYMENT_GATEWAY.CAMPUS_CARD }}
                    disabled={isItemDisabled}
                  />
                }
                onClick={() =>
                  isDeleteMode
                    ? handleSetSelectedPaymentMethodToDelete(card)
                    : handleSetSelectedPaymentMethod({
                        kind: PAYMENT_GATEWAY.CAMPUS_CARD,
                        card,
                      })
                }
              />
            );
          })
        ) : (
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.campusCard.empty"
            defaultText="No campus card linked yet."
            variant="Inter/Body-sm"
            color="text.secondary"
            sx={{ paddingY: 1.5 }}
          />
        )}
      </Stack>

      {/* ACTIONS */}
      {isDeleteMode ? (
        <CardDeleteConfirmActions
          onCancel={handleCancelDeleteMode}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeletingPaymentMethod}
          canConfirm={!!selectedPaymentMethodToDelete}
        />
      ) : (
        <CardActions
          onAdd={() => handleSetPaymentStep(PAYMENT_STEP.ADD_CAMPUS_CARD)}
          onDelete={handleEnterDeleteMode}
          showDelete={cards.length > 0}
        />
      )}
    </>
  );
};
